import React, { useState, useEffect, useMemo, useCallback } from "react";
import Pagination from "../../components/Pagination.tsx";
import { useAllInfluencers, useDeleteUser, useResetUserPassword, useUserDetails } from "../../hooks/useAdmin.ts";
import { 
  FaEye, FaTimes, FaSearch, FaEnvelope, FaPhone, FaCalendar, 
  FaMapMarkerAlt, FaUser, FaLink, FaKey, FaExclamationTriangle,
  FaCheckCircle, FaUserCircle, FaSort, FaSortUp, FaSortDown,
  FaFilter, FaFacebook, FaInstagram, FaTwitter, FaYoutube,
  FaGlobe, FaLink as FaLinkIcon
} from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";
import PasswordResetModal from "../../components/common/PasswordResetModal.tsx";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from "@tanstack/react-table";

interface Influencer {
  user_id: number;
  email: string;
  role: string;
  profile: {
    first_name: string;
    last_name?: string;
    phone_number?: string;
    date_of_birth?: string;
    gender?: string;
    location?: string;
    bio?: string;
    profile_picture?: string;
  };
}

interface UserProfile {
  user: {
    email: string;
    role: string;
  };
  first_name: string;
  last_name: string;
  phone_number: string;
  date_of_birth: string;
  profile_picture: string | null;
}

interface InfluencerData {
  id: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
  others: any[];
  interests: string[];
  district: string;
  state: string;
  pincode: string;
  profile_links: Record<string, any>;
  profile: string;
}

interface UserDetailsResponse {
  profile: UserProfile;
  influencer_data: InfluencerData;
}

const AdminInfluencerData: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  
  // State
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [filteredInfluencers, setFilteredInfluencers] = useState<Influencer[]>([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState<Influencer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'incomplete'>('all');

  // Hooks
  const { data: influencersData, isLoading: isInfluencersLoading } = useAllInfluencers();
  const deleteUserMutation = useDeleteUser();
  const resetUserPasswordMutation = useResetUserPassword();
  const { data: userDetailsData, isLoading: isDetailsLoading, error: detailsErrorObject } = useUserDetails(selectedUserId || 0);

  const detailsError = detailsErrorObject ? "Failed to load user details." : "";
  const userDetails = userDetailsData;

  const url = process.env.REACT_APP_DB_URI;

  useEffect(() => {
    if (influencersData) {
      setInfluencers(influencersData);
      setFilteredInfluencers(influencersData);
    }
  }, [influencersData]);

  // Filter and search
  useEffect(() => {
    let filtered = [...influencers];

    // Apply search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(influencer => {
        const name = `${influencer.profile.first_name} ${influencer.profile.last_name || ''}`.toLowerCase();
        const email = influencer.email.toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }

    // Apply status filter
    if (filterStatus === 'active') {
      filtered = filtered.filter(item => 
        item.profile.phone_number && 
        !item.profile.phone_number.includes('Error')
      );
    } else if (filterStatus === 'incomplete') {
      filtered = filtered.filter(item => 
        !item.profile.phone_number || 
        item.profile.phone_number.includes('Error')
      );
    }

    setFilteredInfluencers(filtered);
    setCurrentPage(1);
  }, [searchTerm, influencers, filterStatus]);

  const getFullImageUrl = (imagePath: string | null) => {
    if (!imagePath) return "/default-avatar.png";
    if (imagePath.startsWith('http')) return imagePath;
    const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
    const baseUrl = process.env.REACT_APP_PF || url;
    return `${baseUrl}/${cleanPath}`;
  };

  const handleViewInfluencer = (influencer: Influencer) => {
    setSelectedInfluencer(influencer);
    setSelectedUserId(influencer.user_id);
    setIsModalOpen(true);
  };

  const handleDelete = (userId: number) => {
    setUserToDelete(userId);
    setIsDeletePopupOpen(true);
  };

  const confirmDelete = async () => {
    if (userToDelete === null) {
      setError("No influencer selected for deletion.");
      return;
    }

    setIsDeleting(true);
    setError("");

    try {
      await deleteUserMutation.mutateAsync(userToDelete);
      setInfluencers(prev => prev.filter(influencer => influencer.user_id !== userToDelete));
      setIsDeletePopupOpen(false);
      setUserToDelete(null);
    } catch (err: any) {
      console.error("Delete failed:", err);
      setError(err.message || "Failed to delete influencer. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInfluencer(null);
    setSelectedUserId(null);
  };

  const handlePasswordReset = async (newPassword: string, confirmPassword: string) => {
    if (!selectedInfluencer) throw new Error("No influencer selected");

    try {
      await resetUserPasswordMutation.mutateAsync({
        userId: selectedInfluencer.user_id,
        newPassword,
        confirmPassword
      });
    } catch (e) {
      throw new Error("Failed to reset password");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatInterests = (interests: string[]) => {
    return interests
      .map(interest => interest.replace(/[\[\]"]/g, '').trim())
      .filter(interest => interest.length > 0);
  };

  const formatProfileLinks = (profileLinks: Record<string, any>) => {
    return Object.entries(profileLinks)
      .filter(([_, data]) => data && typeof data === 'object' && data.link)
      .map(([platform, data]) => ({
        platform,
        url: data.link,
        status: data.status || 'UNKNOWN'
      }));
  };

  const getPlatformIcon = (platform: string) => {
    const p = platform.toLowerCase();
    if (p.includes('facebook') || p.includes('fb')) return <FaFacebook />;
    if (p.includes('instagram') || p.includes('ig')) return <FaInstagram />;
    if (p.includes('twitter') || p.includes('x')) return <FaTwitter />;
    if (p.includes('youtube') || p.includes('yt')) return <FaYoutube />;
    return <FaGlobe />;
  };

  const getProfileCompletion = (influencer: Influencer) => {
    let completed = 0;
    const total = 4;
    if (influencer.profile.phone_number && !influencer.profile.phone_number.includes('Error')) completed++;
    if (influencer.profile.date_of_birth) completed++;
    if (influencer.profile.profile_picture) completed++;
    if (influencer.profile.location) completed++;
    return Math.round((completed / total) * 100);
  };

  // Table columns
  const columnHelper = createColumnHelper<Influencer>();

  const columns: ColumnDef<Influencer, any>[] = [
    columnHelper.accessor((row) => `${row.profile.first_name} ${row.profile.last_name || ''}`, {
      id: 'name',
      header: 'Name',
      cell: (info) => (
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
            style={{ backgroundColor: currentTheme.surface }}
          >
            {info.row.original.profile.profile_picture ? (
              <img 
                src={getFullImageUrl(info.row.original.profile.profile_picture)}
                alt={info.getValue()}
                className="w-full h-full object-cover"
                onError={(e) => (e.target as HTMLImageElement).src = "/default-avatar.png"}
              />
            ) : (
              <FaUserCircle className="w-full h-full" style={{ color: currentTheme.textSecondary }} />
            )}
          </div>
          <span style={{ color: currentTheme.text }}>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor('email', {
      header: 'Email',
      cell: (info) => (
        <div className="flex items-center gap-2">
          <FaEnvelope size={12} style={{ color: currentTheme.textSecondary }} />
          <span style={{ color: currentTheme.text }}>{info.getValue()}</span>
        </div>
      ),
    }),
    columnHelper.accessor('profile.phone_number', {
      header: 'Mobile',
      cell: (info) => {
        const phone = info.getValue();
        const isValid = phone && !phone.includes('Error');
        return (
          <div className="flex items-center gap-2">
            <FaPhone size={12} style={{ color: isValid ? currentTheme.success : currentTheme.textSecondary }} />
            <span style={{ color: isValid ? currentTheme.text : currentTheme.textSecondary }}>
              {isValid ? phone : 'Not provided'}
            </span>
          </div>
        );
      },
    }),
    columnHelper.accessor('profile.date_of_birth', {
      header: 'DOB',
      cell: (info) => {
        const dob = info.getValue();
        return (
          <div className="flex items-center gap-2">
            <FaCalendar size={12} style={{ color: currentTheme.textSecondary }} />
            <span style={{ color: currentTheme.text }}>{dob ? formatDate(dob) : '-'}</span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'completion',
      header: 'Profile',
      cell: (props) => {
        const percentage = getProfileCompletion(props.row.original);
        return (
          <div className="flex items-center gap-2">
            <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: currentTheme.border }}>
              <div 
                className="h-full rounded-full transition-all"
                style={{ 
                  width: `${percentage}%`,
                  backgroundColor: percentage > 70 ? currentTheme.success : percentage > 30 ? currentTheme.warning : currentTheme.error
                }}
              />
            </div>
            <span className="text-xs" style={{ color: currentTheme.textSecondary }}>{percentage}%</span>
          </div>
        );
      },
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-center">Actions</div>,
      cell: (props) => (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => handleViewInfluencer(props.row.original)}
            className="p-1.5 rounded transition-colors hover:opacity-80"
            style={{ color: currentTheme.primary, backgroundColor: `${currentTheme.primary}20` }}
            title="View Details"
          >
            <FaEye size={16} />
          </button>
          <button
            onClick={() => handleDelete(props.row.original.user_id)}
            className="p-1.5 rounded transition-colors hover:opacity-80"
            style={{ color: currentTheme.error, backgroundColor: `${currentTheme.error}20` }}
            disabled={isDeleting}
            title="Delete User"
          >
            <MdDelete size={16} />
          </button>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: filteredInfluencers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(filteredInfluencers.length / rowsPerPage),
  });

  // Pagination
  const totalPages = Math.ceil(filteredInfluencers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const sortedRows = table.getRowModel().rows;
  const paginatedRows = sortedRows.slice(startIndex, endIndex);

  // Stats
  const stats = {
    total: influencers.length,
    active: influencers.filter(i => i.profile.phone_number && !i.profile.phone_number.includes('Error')).length,
    incomplete: influencers.filter(i => !i.profile.phone_number || i.profile.phone_number.includes('Error')).length,
  };

  return (
    <div className="pb-4" style={{ backgroundColor: currentTheme.background }}>
      {/* Error Toast */}
      {error && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg animate-slideIn" 
             style={{ backgroundColor: currentTheme.error, color: 'white' }}>
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="rounded-lg p-4 shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Total Influencers</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.text }}>{stats.total}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: `${currentTheme.primary}20` }}>
              <FaUser style={{ color: currentTheme.primary }} />
            </div>
          </div>
        </div>
        
        <div className="rounded-lg p-4 shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Active Profiles</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.success }}>{stats.active}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: `${currentTheme.success}20` }}>
              <FaCheckCircle style={{ color: currentTheme.success }} />
            </div>
          </div>
        </div>
        
        <div className="rounded-lg p-4 shadow-sm" style={{ backgroundColor: currentTheme.surface }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm" style={{ color: currentTheme.textSecondary }}>Incomplete Profiles</p>
              <p className="text-2xl font-bold" style={{ color: currentTheme.warning }}>{stats.incomplete}</p>
            </div>
            <div className="p-3 rounded-full" style={{ backgroundColor: `${currentTheme.warning}20` }}>
              <FaExclamationTriangle style={{ color: currentTheme.warning }} />
            </div>
          </div>
        </div>
      </div> */}

      {/* Search and Filters */}
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <div className="flex-1 min-w-[300px]">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" 
                     style={{ color: currentTheme.textSecondary }} />
            <input
              type="text"
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-all"
              style={{ 
                backgroundColor: currentTheme.surface, 
                color: currentTheme.text,
                borderColor: currentTheme.border,
                caretColor: currentTheme.primary
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          {(['all', 'active', 'incomplete'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className="px-4 py-2 rounded-lg capitalize transition-colors"
              style={{
                backgroundColor: filterStatus === status ? currentTheme.primary : currentTheme.surface,
                color: filterStatus === status ? currentTheme.inverseText : currentTheme.text,
                border: `1px solid ${currentTheme.border}`
              }}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg shadow-lg overflow-hidden" style={{ backgroundColor: currentTheme.surface }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} style={{ backgroundColor: currentTheme.primary }}>
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      className="px-4 py-3 text-left text-sm font-medium cursor-pointer select-none"
                      style={{ color: currentTheme.inverseText }}
                    >
                      <div className="flex items-center gap-2">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <span className="flex flex-col text-[10px] leading-none">
                            <span style={{ opacity: header.column.getIsSorted() === 'asc' ? 1 : 0.5 }}>▲</span>
                            <span style={{ opacity: header.column.getIsSorted() === 'desc' ? 1 : 0.5 }}>▼</span>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row, index) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:opacity-90"
                    style={{ backgroundColor: index % 2 === 0 ? currentTheme.surface : currentTheme.card }}
                  >
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-3 text-sm" style={{ color: currentTheme.text }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center" style={{ color: currentTheme.textSecondary }}>
                    {searchTerm ? "No matching influencers found" : "No influencers available"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t" 
             style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border }}>
          <div className="flex items-center gap-4">
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 rounded border text-sm focus:outline-none focus:ring-2"
              style={{
                backgroundColor: currentTheme.surface,
                color: currentTheme.text,
                borderColor: currentTheme.border
              }}
            >
              {[15, 25, 50, 100].map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <span className="text-sm" style={{ color: currentTheme.textSecondary }}>
              Showing {filteredInfluencers.length === 0 ? 0 : startIndex + 1}-
              {Math.min(endIndex, filteredInfluencers.length)} of {filteredInfluencers.length}
            </span>
          </div>
          <Pagination 
            currentPage={currentPage} 
            totalPages={totalPages} 
            onPageChange={setCurrentPage} 
          />
        </div>
      </div>

      {/* User Details Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-[101]" 
             style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="w-[95%] max-w-6xl max-h-[95vh] rounded-lg shadow-xl flex flex-col overflow-hidden"
               style={{ backgroundColor: currentTheme.surface }}>
            
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b"
                 style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
              <h2 className="text-xl font-semibold" style={{ color: currentTheme.inverseText }}>
                User Details
              </h2>
              <button onClick={closeModal} className="p-1 rounded hover:opacity-80 transition-opacity"
                      style={{ color: currentTheme.inverseText }}>
                <FaTimes size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isDetailsLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" 
                       style={{ borderColor: currentTheme.primary }} />
                </div>
              ) : detailsError ? (
                <div className="text-center py-12" style={{ color: currentTheme.error }}>
                  {detailsError}
                </div>
              ) : userDetails ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Left Column - Profile */}
                  <div className="lg:col-span-1 space-y-6">
                    
                    {/* Profile Card */}
                    <div className="rounded-lg overflow-hidden shadow-sm" style={{ backgroundColor: currentTheme.card }}>
                      <div className="p-6 text-center">
                        <div className="w-28 h-28 mx-auto mb-4 rounded-full overflow-hidden border-4"
                             style={{ borderColor: currentTheme.primary }}>
                          <img
                            src={getFullImageUrl(userDetails.profile.profile_picture)}
                            alt={`${userDetails.profile.first_name} ${userDetails.profile.last_name}`}
                            className="w-full h-full object-cover"
                            onError={(e) => (e.target as HTMLImageElement).src = "/default-avatar.png"}
                          />
                        </div>
                        <h3 className="text-xl font-semibold mb-1" style={{ color: currentTheme.text }}>
                          {userDetails.profile.first_name} {userDetails.profile.last_name}
                        </h3>
                        <span className="inline-block px-3 py-1 rounded-full text-xs"
                              style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}>
                          {userDetails.profile.user.role}
                        </span>
                      </div>

                      <div className="border-t p-4 space-y-3" style={{ borderColor: currentTheme.border }}>
                        <div className="flex items-start gap-3">
                          <FaEnvelope className="mt-1" style={{ color: currentTheme.primary }} />
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Email</p>
                            <p className="text-sm break-all" style={{ color: currentTheme.text }}>
                              {userDetails.profile.user.email}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <FaPhone className="mt-1" style={{ color: currentTheme.primary }} />
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Phone</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {userDetails.profile.phone_number?.includes('Error') 
                                ? 'Not available' 
                                : userDetails.profile.phone_number || 'Not provided'}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <FaCalendar className="mt-1" style={{ color: currentTheme.primary }} />
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Date of Birth</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {userDetails.profile.date_of_birth 
                                ? formatDate(userDetails.profile.date_of_birth) 
                                : 'Not provided'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Location Card */}
                    {userDetails.influencer_data && (
                      <div className="rounded-lg overflow-hidden shadow-sm" style={{ backgroundColor: currentTheme.card }}>
                        <div className="px-4 py-3 border-b" 
                             style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
                          <h4 className="font-medium flex items-center gap-2" style={{ color: currentTheme.inverseText }}>
                            <FaMapMarkerAlt /> Location
                          </h4>
                        </div>
                        <div className="p-4 space-y-3">
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>District</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {userDetails.influencer_data.district || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>State</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {userDetails.influencer_data.state || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs" style={{ color: currentTheme.textSecondary }}>Pincode</p>
                            <p className="text-sm" style={{ color: currentTheme.text }}>
                              {userDetails.influencer_data.pincode || 'Not provided'}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Right Column - Details */}
                  <div className="lg:col-span-2 space-y-6">
                    {userDetails.influencer_data && (
                      <div className="rounded-lg overflow-hidden shadow-sm" style={{ backgroundColor: currentTheme.card }}>
                        
                        {/* Header */}
                        <div className="px-4 py-3 border-b" 
                             style={{ backgroundColor: currentTheme.primary, borderColor: currentTheme.border }}>
                          <h4 className="font-medium flex items-center gap-2" style={{ color: currentTheme.inverseText }}>
                            <FaUser /> Influencer Information
                          </h4>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-6">
                          
                          {/* Status & ID */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs mb-1" style={{ color: currentTheme.textSecondary }}>Account Status</p>
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs`}
                                    style={{
                                      backgroundColor: !userDetails.influencer_data.is_deleted 
                                        ? `${currentTheme.success}20` 
                                        : `${currentTheme.error}20`,
                                      color: !userDetails.influencer_data.is_deleted 
                                        ? currentTheme.success 
                                        : currentTheme.error
                                    }}>
                                {!userDetails.influencer_data.is_deleted ? (
                                  <><FaCheckCircle size={12} /> Active</>
                                ) : (
                                  <><FaExclamationTriangle size={12} /> Deleted</>
                                )}
                              </span>
                            </div>
                            <div>
                              <p className="text-xs mb-1" style={{ color: currentTheme.textSecondary }}>Influencer ID</p>
                              <p className="text-xs font-mono p-1 rounded" 
                                 style={{ backgroundColor: currentTheme.surface, color: currentTheme.text }}>
                                {userDetails.influencer_data.id}
                              </p>
                            </div>
                          </div>

                          {/* Dates */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs mb-1" style={{ color: currentTheme.textSecondary }}>Created At</p>
                              <p className="text-sm font-medium" style={{ color: currentTheme.text }}>
                                {formatDate(userDetails.influencer_data.created_at)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs mb-1" style={{ color: currentTheme.textSecondary }}>Last Updated</p>
                              <p className="text-sm font-medium" style={{ color: currentTheme.text }}>
                                {formatDate(userDetails.influencer_data.updated_at)}
                              </p>
                            </div>
                          </div>

                          {/* Interests */}
                          {userDetails.influencer_data.interests?.length > 0 && (
                            <div>
                              <p className="text-xs mb-2" style={{ color: currentTheme.textSecondary }}>Interests</p>
                              <div className="flex flex-wrap gap-2">
                                {formatInterests(userDetails.influencer_data.interests).map((interest, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 rounded-full text-xs"
                                    style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                                  >
                                    {interest}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Profile Links */}
                          {userDetails.influencer_data.profile_links && 
                           Object.keys(userDetails.influencer_data.profile_links).length > 0 && (
                            <div>
                              <p className="text-xs mb-3 flex items-center gap-2" style={{ color: currentTheme.textSecondary }}>
                                <FaLinkIcon /> Social Media Links
                              </p>
                              <div className="space-y-2">
                                {formatProfileLinks(userDetails.influencer_data.profile_links).map((link, index) => (
                                  <a
                                    key={index}
                                    href={link.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-3 p-3 rounded-lg border transition-all hover:opacity-80"
                                    style={{ 
                                      backgroundColor: currentTheme.surface, 
                                      borderColor: currentTheme.border,
                                      color: currentTheme.primary
                                    }}
                                  >
                                    <span className="text-lg" style={{ color: currentTheme.primary }}>
                                      {getPlatformIcon(link.platform)}
                                    </span>
                                    <span className="flex-1 text-sm truncate" style={{ color: currentTheme.text }}>
                                      {link.url}
                                    </span>
                                    <span className="text-xs px-2 py-1 rounded" 
                                          style={{ 
                                            backgroundColor: `${currentTheme.primary}20`,
                                            color: currentTheme.primary 
                                          }}>
                                      {link.platform}
                                    </span>
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* No Links */}
                          {(!userDetails.influencer_data.profile_links || 
                            Object.keys(userDetails.influencer_data.profile_links).length === 0) && (
                            <div className="text-center py-4" style={{ color: currentTheme.textSecondary }}>
                              No social media links provided
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex justify-between" 
                 style={{ borderColor: currentTheme.border, backgroundColor: currentTheme.card }}>
              <button
                onClick={() => setIsPasswordResetModalOpen(true)}
                className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 flex items-center gap-2"
                style={{ 
                  backgroundColor: currentTheme.surface, 
                  color: currentTheme.primary, 
                  border: `1px solid ${currentTheme.border}`
                }}
              >
                <FaKey size={14} />
                Reset Password
              </button>
              <button
                onClick={closeModal}
                className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
                style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeletePopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="p-6 rounded-lg shadow-xl max-w-sm w-full" style={{ backgroundColor: currentTheme.surface }}>
            <div className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4"
                   style={{ backgroundColor: `${currentTheme.error}20` }}>
                <FaExclamationTriangle style={{ color: currentTheme.error }} size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2" style={{ color: currentTheme.text }}>Confirm Deletion</h3>
              <p className="mb-6" style={{ color: currentTheme.textSecondary }}>
                Are you sure you want to delete this influencer? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={confirmDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 disabled:opacity-50"
                  style={{ backgroundColor: currentTheme.error, color: currentTheme.inverseText }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
                <button
                  onClick={() => setIsDeletePopupOpen(false)}
                  disabled={isDeleting}
                  className="px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80 border"
                  style={{ 
                    backgroundColor: currentTheme.surface, 
                    color: currentTheme.text, 
                    borderColor: currentTheme.border 
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {selectedInfluencer && userDetails && (
        <PasswordResetModal
          isOpen={isPasswordResetModalOpen}
          onClose={() => setIsPasswordResetModalOpen(false)}
          onSubmit={handlePasswordReset}
          userName={`${userDetails.profile.first_name} ${userDetails.profile.last_name}`}
          userEmail={userDetails.profile.user.email}
          userRole={userDetails.profile.user.role}
        />
      )}
    </div>
  );
};

export default AdminInfluencerData;